import './ImageGallery.css'

import { useRef, useState, useEffect, useMemo } from 'react'

import { Icon } from './Icon'

export type ImageGalleryProps = {
  images: string[]
  alt?: string
  targetHeight?: number
  gap?: number
  onEdit?: () => void
}

type ImageDimensions = {
  src: string
  aspectRatio: number
  loaded: boolean
}

type PageItem =
  | { type: 'image'; src: string; width: number; height: number }
  | { type: 'spacer'; width: number; height: number }

type PageLayout = {
  items: PageItem[]
  width: number
}

export type GallerySpacerProps = {
  className?: string
  width?: number
  height?: number
  showNoPhotosMessage?: boolean
}

export function GallerySpacer(props: GallerySpacerProps) {
  const { className, width, height, showNoPhotosMessage } = props

  return (
    <div
      className={className ? `image-gallery__spacer ${className}` : 'image-gallery__spacer'}
      style={{
        width: width !== undefined ? `${width}px` : '100%',
        height: height !== undefined ? `${height}px` : '100%',
      }}
    >
      <div className="image-gallery__spacer-blob image-gallery__spacer-blob--1" />
      <div className="image-gallery__spacer-blob image-gallery__spacer-blob--2" />
      <div className="image-gallery__spacer-blob image-gallery__spacer-blob--3" />
      {showNoPhotosMessage && (
        <div className="image-gallery__spacer-content">
          <span className="image-gallery__spacer-label">No photos</span>
        </div>
      )}
    </div>
  )
}

export function ImageGallery(props: ImageGalleryProps) {
  const { images, alt = 'Photo', targetHeight = 400, gap = 0, onEdit } = props

  const containerRef = useRef<HTMLDivElement>(null)
  const galleryRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState(0)
  const [imageDimensions, setImageDimensions] = useState<ImageDimensions[]>([])
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const [isDragging, setIsDragging] = useState(false)
  const [dragStartX, setDragStartX] = useState(0)
  const [scrollStartX, setScrollStartX] = useState(0)
  const velocityRef = useRef({ x: 0, time: 0, lastX: 0 })

  // Observe container width
  useEffect(() => {
    const gallery = galleryRef.current
    if (!gallery) return

    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (entry) {
        setContainerWidth(entry.contentRect.width)
      }
    })

    resizeObserver.observe(gallery)
    return () => resizeObserver.disconnect()
  }, [])

  // Load image dimensions
  useEffect(() => {
    if (images.length === 0) {
      setImageDimensions([])
      return
    }

    const loadImage = (src: string): Promise<ImageDimensions> => {
      return new Promise((resolve) => {
        const img = new Image()
        img.onload = () => {
          resolve({
            src,
            aspectRatio: img.naturalWidth / img.naturalHeight,
            loaded: true,
          })
        }
        img.onerror = () => {
          resolve({
            src,
            aspectRatio: 3 / 4, // Default portrait aspect ratio
            loaded: false,
          })
        }
        img.src = src
      })
    }

    Promise.all(images.map(loadImage)).then(setImageDimensions)
  }, [images])

  // Use DP to find the optimal page breaks that minimize total cropped area
  // from object-fit: cover. Images maintain their original order — only the
  // break points between pages are optimized.
  // Last page uses natural sizing + spacer if stretching would exceed maxScale.
  const maxScale = 1.3

  const pageLayouts = useMemo<PageLayout[]>(() => {
    if (containerWidth === 0 || imageDimensions.length === 0) return []

    const n = imageDimensions.length
    const naturalWidths = imageDimensions.map((img) => targetHeight * img.aspectRatio)

    // Scale factor for placing images i..j-1 on one page.
    // < 1: images narrower than natural (horizontal crop)
    // > 1: images wider than natural (vertical crop via zoom)
    const scaleForPage = (i: number, j: number) => {
      let totalNatural = 0
      for (let k = i; k < j; k += 1) totalNatural += naturalWidths[k]!
      const totalGaps = (j - i - 1) * gap
      // +1 so the last image's border extends past the edge
      const available = containerWidth - totalGaps + 1
      return available / totalNatural
    }

    // Cost = fraction of image area cropped by object-fit: cover, summed
    // across images on the page. Directly minimizes visible area lost.
    const pageCost = (i: number, j: number) => {
      const scale = scaleForPage(i, j)
      const cropFraction = scale > 1 ? 1 - 1 / scale : 1 - scale
      return cropFraction * (j - i)
    }

    // dp[j] = minimum total crop cost for laying out images 0..j-1
    const dp = new Array(n + 1).fill(Infinity) as number[]
    const prev = new Array(n + 1).fill(0) as number[]
    dp[0] = 0

    for (let j = 1; j <= n; j += 1) {
      for (let i = j - 1; i >= 0; i -= 1) {
        const cost = dp[i]! + pageCost(i, j)
        if (cost < dp[j]!) {
          dp[j] = cost
          prev[j] = i
        }
      }
    }

    // Reconstruct optimal page boundaries
    const breaks: number[] = []
    let j = n
    while (j > 0) {
      breaks.unshift(prev[j]!)
      j = prev[j]!
    }
    breaks.push(n)

    // Build page layouts from the optimal partition
    const layouts: PageLayout[] = []
    for (let p = 0; p < breaks.length - 1; p += 1) {
      const start = breaks[p]!
      const end = breaks[p + 1]!
      const isLastPage = p === breaks.length - 2
      const page = imageDimensions.slice(start, end)

      const totalNatural = page.reduce((sum, img) => sum + targetHeight * img.aspectRatio, 0)
      const totalGaps = (page.length - 1) * gap
      const available = containerWidth - totalGaps + 1
      const scale = available / totalNatural

      if (isLastPage && scale > maxScale) {
        // Last page would stretch too much — use natural widths + spacer
        const items: PageItem[] = page.map((img) => ({
          type: 'image' as const,
          src: img.src,
          width: targetHeight * img.aspectRatio,
          height: targetHeight,
        }))

        const spacerWidth = containerWidth - (totalNatural + totalGaps)
        if (spacerWidth > 50) {
          items.push({ type: 'spacer' as const, width: spacerWidth, height: targetHeight })
        }

        layouts.push({ items, width: containerWidth })
      } else {
        // Scale images to fill the page
        const scaledWidths = page.map((img) => targetHeight * img.aspectRatio * scale)
        const flooredWidths = scaledWidths.map((w, idx) =>
          idx < scaledWidths.length - 1 ? Math.floor(w) : w,
        )
        const usedWidth = flooredWidths.slice(0, -1).reduce((sum, w) => sum + w, 0)
        flooredWidths[flooredWidths.length - 1] = available - usedWidth

        layouts.push({
          items: page.map((img, idx) => ({
            type: 'image' as const,
            src: img.src,
            width: flooredWidths[idx]!,
            height: targetHeight,
          })),
          width: containerWidth,
        })
      }
    }

    return layouts
  }, [containerWidth, imageDimensions, targetHeight, gap, maxScale])

  // Flatten pages into single item array for rendering
  const layoutItems = useMemo(() => {
    return pageLayouts.flatMap((page) => page.items)
  }, [pageLayouts])

  // Calculate scroll positions for each page (for snapping)
  const pagePositions = useMemo(() => {
    const positions: number[] = [0]
    let cumulative = 0
    for (let i = 0; i < pageLayouts.length - 1; i += 1) {
      cumulative += pageLayouts[i]!.width + gap
      positions.push(cumulative)
    }
    return positions
  }, [pageLayouts, gap])

  // Find nearest page position for snapping
  const snapToNearestPage = (currentScroll: number, velocity: number) => {
    const container = containerRef.current
    if (!container || pagePositions.length === 0) return

    // Apply velocity to predict where user intended to go
    const predictedScroll = currentScroll - velocity * 100

    // Find nearest page position
    let nearestIndex = 0
    let nearestDistance = Math.abs(predictedScroll - pagePositions[0]!)

    for (let i = 1; i < pagePositions.length; i += 1) {
      const distance = Math.abs(predictedScroll - pagePositions[i]!)
      if (distance < nearestDistance) {
        nearestDistance = distance
        nearestIndex = i
      }
    }

    const targetPosition = pagePositions[nearestIndex]
    if (targetPosition !== undefined) {
      container.scrollTo({
        left: targetPosition,
        behavior: 'smooth',
      })
    }
  }

  const updateScrollState = () => {
    const container = containerRef.current
    if (!container) return

    const { scrollLeft, scrollWidth, clientWidth } = container
    setCanScrollLeft(scrollLeft > 0)
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1)
  }

  useEffect(() => {
    updateScrollState()
  }, [layoutItems])

  const handleScroll = () => {
    updateScrollState()
  }

  const scrollToPage = (direction: 'left' | 'right') => {
    const container = containerRef.current
    if (!container || pagePositions.length === 0) return

    const currentScroll = container.scrollLeft

    // Find current page index
    let currentIndex = 0
    for (let i = pagePositions.length - 1; i >= 0; i -= 1) {
      if (currentScroll >= pagePositions[i]! - 10) {
        currentIndex = i
        break
      }
    }

    // Navigate to next/previous page
    const targetIndex =
      direction === 'left'
        ? Math.max(0, currentIndex - 1)
        : Math.min(pagePositions.length - 1, currentIndex + 1)

    const targetPosition = pagePositions[targetIndex]
    if (targetPosition !== undefined) {
      container.scrollTo({
        left: targetPosition,
        behavior: 'smooth',
      })
    }
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    const container = containerRef.current
    if (!container) return

    setIsDragging(false)
    setDragStartX(e.clientX)
    setScrollStartX(container.scrollLeft)
    velocityRef.current = { x: 0, time: Date.now(), lastX: e.clientX }
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (dragStartX === 0) return
    const container = containerRef.current
    if (!container) return

    const diff = e.clientX - dragStartX
    if (Math.abs(diff) > 5) {
      setIsDragging(true)
      container.scrollLeft = scrollStartX - diff

      const now = Date.now()
      const dt = now - velocityRef.current.time
      if (dt > 0) {
        velocityRef.current = {
          x: (e.clientX - velocityRef.current.lastX) / dt,
          time: now,
          lastX: e.clientX,
        }
      }
    }
  }

  const handleDragEnd = () => {
    const container = containerRef.current
    if (!container) return

    if (isDragging) {
      const velocity = velocityRef.current.x
      snapToNearestPage(container.scrollLeft, velocity)
    }

    setIsDragging(false)
    setDragStartX(0)
    setScrollStartX(0)
  }

  const handleMouseUp = () => {
    // If we weren't dragging, this is a click - trigger onEdit
    if (!isDragging && onEdit) {
      onEdit()
    }
    handleDragEnd()
  }

  const handleMouseLeave = () => {
    if (dragStartX !== 0) {
      handleDragEnd()
    }
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0]
    if (!touch) return
    const container = containerRef.current
    if (!container) return

    setIsDragging(false)
    setDragStartX(touch.clientX)
    setScrollStartX(container.scrollLeft)
    velocityRef.current = { x: 0, time: Date.now(), lastX: touch.clientX }
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    if (dragStartX === 0) return
    const touch = e.touches[0]
    if (!touch) return
    const container = containerRef.current
    if (!container) return

    const diff = touch.clientX - dragStartX
    if (Math.abs(diff) > 5) {
      setIsDragging(true)
      container.scrollLeft = scrollStartX - diff

      const now = Date.now()
      const dt = now - velocityRef.current.time
      if (dt > 0) {
        velocityRef.current = {
          x: (touch.clientX - velocityRef.current.lastX) / dt,
          time: now,
          lastX: touch.clientX,
        }
      }
    }
  }

  const handleTouchEnd = () => {
    // If we weren't dragging, this is a tap - trigger onEdit
    if (!isDragging && onEdit) {
      onEdit()
    }
    handleDragEnd()
  }

  if (images.length === 0) {
    return (
      <div
        className={`image-gallery ${onEdit ? 'image-gallery--editable' : ''}`}
        ref={galleryRef}
        onClick={onEdit}
      >
        <GallerySpacer height={targetHeight} showNoPhotosMessage />
        {onEdit && <div className="image-gallery__edit-overlay">Edit photos</div>}
      </div>
    )
  }

  const classNames = [
    'image-gallery',
    isDragging && 'image-gallery--dragging',
    onEdit && 'image-gallery--editable',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div
      className={classNames}
      ref={galleryRef}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {canScrollLeft && (
        <button
          className="image-gallery__nav image-gallery__nav--left"
          onClick={() => scrollToPage('left')}
          aria-label="Scroll left"
        >
          <Icon name="chevron-left" />
        </button>
      )}

      <div
        className="image-gallery__container"
        ref={containerRef}
        onScroll={handleScroll}
        style={{ gap: `${gap}px` }}
        tabIndex={0}
        aria-label="Photo gallery"
        role="region"
      >
        <div className="image-gallery__track">
          {layoutItems.map((item, index) =>
            item.type === 'image' ? (
              <img
                key={index}
                className="image-gallery__image"
                src={item.src}
                alt={`${alt} ${index + 1}`}
                style={{
                  width: `${item.width}px`,
                  height: `${item.height}px`,
                }}
                draggable={false}
              />
            ) : (
              <GallerySpacer key={`spacer-${index}`} width={item.width} height={item.height} />
            ),
          )}
        </div>
      </div>

      {canScrollRight && (
        <button
          className="image-gallery__nav image-gallery__nav--right"
          onClick={() => scrollToPage('right')}
          aria-label="Scroll right"
        >
          <Icon name="chevron-right" />
        </button>
      )}

      {onEdit && <div className="image-gallery__edit-overlay">Edit photos</div>}
    </div>
  )
}
